import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import './ArticleDetailPage.scss';
import strapiClient from '../../services/strapi/config';
import { formatDateNumberInText, safeHtml } from '../../services/helpers';
import SvgIcons from '../../services/SvgIcons';
import { baseStrapiURL, baseURL } from '../../services/config';
import { useMediaQuery } from 'react-responsive';
import Loading from '../../components/Loading/Loading';
import BackgroundImageWithSkeleton from '../../components/BackgroundImageWithSkeleton/BackgroundImageWithSkeleton';
import { useBlogNavigation } from '../../hooks/useBlogNavigation';

const ArticleDetailPage = () => {
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const { slug } = useParams();
    const { navigateBackToBlog } = useBlogNavigation();

    // Fonction qui permet de retirer les styles voulus dans le contenu html issu de Strapi (fonction créée pour les vieillees newsletter extraites de wix ou systemio)
    const removeMultipleInlineStyles = (html, stylesToRemove) => {
        return html.replace(/style=["']([^"']*)["']/gi, (match, styleContent) => {
            // Sépare les propriétés CSS
            const styleArray = styleContent.split(';').map(s => s.trim()).filter(Boolean);

            // Filtre les styles à supprimer
            const filteredStyles = styleArray.filter(style => {
                return !stylesToRemove.some(toRemove => style.includes(toRemove));
            });

            // Reconstruit l'attribut style ou le supprime s'il est vide
            return filteredStyles.length > 0
                ? `style="${filteredStyles.join('; ')}"`
                : '';
        });
    };

    // Fonction pour remplacer les anciens liens par les nouveaux
    const replaceOldLinks = (htmlContent) => {
        if (!htmlContent) return htmlContent;

        const linkMappings = {
            'https://www.sensibilitefeminine.fr/hey-mama-t-es-pas-seule': 'https://www.melaniebouthors.fr/hey-mama-t-es-pas-seule',
            'https://www.sensibilitefeminine.fr/fini-les-crises': 'https://www.melaniebouthors.fr/ateliers/fini-les-crises',
            'https://www.melaniebouthors.fr/fini-les-crises': 'https://www.melaniebouthors.fr/ateliers/fini-les-crises',
            'https://calendly.com/fr/404': 'https://www.resalib.fr/praticien/26593-melanie-bouthors-coach-parental-et-familial-montpellier',
            'https://calendly.com/melaniebouthors': 'https://www.resalib.fr/praticien/26593-melanie-bouthors-coach-parental-et-familial-montpellier'
        };

        let updatedContent = htmlContent;

        Object.entries(linkMappings).forEach(([oldLink, newLink]) => {
            // Remplacer dans les attributs href avec guillemets doubles
            updatedContent = updatedContent.replace(
                new RegExp(`href="${oldLink.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'gi'),
                `href="${newLink}"`
            );
            
            // Remplacer dans les attributs href avec guillemets simples
            updatedContent = updatedContent.replace(
                new RegExp(`href='${oldLink.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}'`, 'gi'),
                `href='${newLink}'`
            );
            
            // Remplacer les liens en texte brut
            updatedContent = updatedContent.replace(
                new RegExp(oldLink.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                newLink
            );
        });

        return updatedContent;
    };

    // Hook pour récupérer la newsletter par slug
    const { 
        data: newsletter, 
        isLoading, 
        error 
    } = useQuery({
        queryKey: ['newsletter-detail', slug],
        queryFn: async () => {
            const response = await strapiClient.get(`/api/newsletter/public/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: 1,
        refetchOnWindowFocus: false
    });

    // Couleurs des catégories
    const getCategoryColor = (category) => {
        const colors = {
            'Sécurité émotionnelle': 'blue',
            'Soutien émotionnel des mamans': 'green', 
            'Coopération': 'purple',
            'Confiance en soi': 'pink',
            'Harcèlement scolaire': 'orange',
            'Accueil émotionnel': 'teal',
            'Injonctions et croyances collectives': 'indigo',
            'Violences éducatives ordinaires': 'red',
        };
        return colors[category] || 'gray';
    };

    // Fonction de partage
    const handleShare = async (platform) => {
        const url = window.location.href;
        const title = newsletter?.Sujet || 'Article';
        const text = newsletter?.Extrait || '';

        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
        };

        if (platform === 'native' && navigator.share) {
            try {
                await navigator.share({
                    title,
                    text,
                    url
                });
            } catch (err) {
                console.log('Partage annulé');
            }
        } else if (shareUrls[platform]) {
            window.open(shareUrls[platform], '_blank', 'width=600,height=400');
        }
    };

    // Gestion des états de chargement et d'erreur
    if (isLoading) {
        return (
            <div className="article-detail">
                <div className="article-detail__loading">
                    <Loading />
                </div>
            </div>
        );
    }

    if (error || !newsletter) {
        return (
            <div className="article-detail">
                <div className="article-detail__error">
                    <h1>Article non trouvé</h1>
                    <p>L'article que vous recherchez n'existe pas ou n'est plus disponible.</p>
                    <Link to="/blog" className="article-detail__back-button">
                        <span className="icon">←</span>
                        Retour au blog
                    </Link>
                </div>
            </div>
        );
    }

    const categoryColor = getCategoryColor(newsletter.Categorie);

    const stylesToRemove = ['color: #0096ff', 'color: #f5a623', 'text-align:justify'];
    const cleanedContent = removeMultipleInlineStyles(newsletter.Contenu, stylesToRemove);
    const finalContent = replaceOldLinks(cleanedContent);

    return (
        <div className="article-detail">
            <Helmet>
                <title>{newsletter.Sujet} - Mélanie Bouthors</title>
                <meta name="description" content={newsletter.Extrait || newsletter.AperçuMail} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={newsletter.Sujet} />
                <meta property="og:description" content={newsletter.Extrait || newsletter.AperçuMail} />
                {newsletter.PhotoMiniature && (
                    <meta property="og:image" content={baseStrapiURL + newsletter.PhotoMiniature.url} />
                )}
                <meta property="og:url" content={`${baseURL}/newsletter/${newsletter.Slug}`} />
                <meta property="article:published_time" content={newsletter.DateEnvoi} />
                <meta property="article:author" content="Mélanie Bouthors" />
                <meta property="article:section" content={newsletter.Categorie} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={newsletter.Sujet} />
                <meta name="twitter:description" content={newsletter.Extrait || newsletter.AperçuMail} />
                {newsletter.PhotoMiniature && (
                    <meta name="twitter:image" content={baseStrapiURL + newsletter.PhotoMiniature.url} />
                )}

                {/* Structure JSON-LD pour le SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": newsletter.Sujet,
                        "description": newsletter.Extrait || newsletter.AperçuMail,
                        "image": newsletter.PhotoMiniature ? baseStrapiURL + newsletter.PhotoMiniature.url : null,
                        "datePublished": newsletter.DateEnvoi,
                        "dateModified": newsletter.updatedAt,
                        "author": {
                            "@type": "Person",
                            "name": "Mélanie Bouthors"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "Mélanie Bouthors",
                            "url": baseURL
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `${baseURL}/newsletter/${newsletter.Slug}`
                        }
                    })}
                </script>
            </Helmet>

            {
                newsletter.PhotoMiniature &&
                        <BackgroundImageWithSkeleton
                            picture={newsletter.PhotoMiniature}
                            width='100%'
                            height={!isMobile ? '529px' : '350px'}  //450px + 79px (navbar)
                            opacityBlock={true}
                            positionMobile='absolute'
                            positionDesktop='fixed'
                        />
            }

            {/* Header de l'article */}
            <header className="article-detail__header" style={{height: !isMobile ? '450px' : '350px'}}>
                {/* Navigation retour */}
                <nav className="article-detail__nav">
                    <button 
                        onClick={() => navigateBackToBlog()}
                        className="article-detail__back-button"
                        aria-label="Retour au blog"
                    >
                        <SvgIcons
                            name={'chevron_back'}
                            width={35}
                            height={35}
                            color={'#F1EBE3'}
                        />
                        Blog
                    </button>
                </nav>

                <h1 className="article-detail__title">{newsletter.Sujet}</h1>

                <span className="article-detail__author" >
                    {newsletter.Auteur || "Mélanie Bouthors"}
                </span>

                {/* Boutons de partage */}
                <div className="article-detail__share">
                    <div className="article-detail__share-buttons">
                        {navigator.share && (
                            <button 
                                onClick={() => handleShare('native')}
                                className="article-detail__share-button"
                                aria-label="Partager"
                            >
                                <span className="icon">↗</span>
                            </button>
                        )}
                        <button 
                            onClick={() => handleShare('facebook')}
                            className="article-detail__share-button article-detail__share-button--facebook"
                            aria-label="Partager sur Facebook"
                        >
                            <span className="icon">F</span>
                        </button>
                        <button 
                            onClick={() => handleShare('twitter')}
                            className="article-detail__share-button article-detail__share-button--twitter"
                            aria-label="Partager sur Twitter"
                        >
                            <span className="icon">𝕏</span>
                        </button>
                        <button 
                            onClick={() => handleShare('linkedin')}
                            className="article-detail__share-button article-detail__share-button--linkedin"
                            aria-label="Partager sur LinkedIn"
                        >
                            <span className="icon">in</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Contenu principal */}
            <main className="article-detail__content">
                <div className="article-detail__meta">
                    <span className={`article-detail__category article-detail__category--${categoryColor}`}
                        style={{ backgroundColor: newsletter.Categorie.Couleur }}
                    >
                        {newsletter.Categorie.Nom}
                    </span>
                    <time className="article-detail__date" dateTime={newsletter.DateEnvoi}>
                        <SvgIcons
                            name={'calendar'}
                            width={30}
                            height={30}
                            color={'#361215'}
                        />
                        {formatDateNumberInText(newsletter.DateEnvoi)}
                    </time>
                </div>
                <div 
                    className="article-detail__body"
                    dangerouslySetInnerHTML={safeHtml(finalContent)}
                />
            </main>

            {/* Navigation vers d'autres articles */}
            <footer className="article-detail__footer">
                <div className="article-detail__cta">
                    <h3>Continuer la lecture</h3>
                    <p>Découvrez d'autres articles sur la parentalité bienveillante</p>
                    <button onClick={() => navigateBackToBlog()} className="article-detail__cta-button">
                        Voir tous les articles
                    </button>
                </div>
            </footer>
        </div>
    );
};

export default ArticleDetailPage;